import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { API, formatApiErrorDetail } from "../lib/api";

export default function AcceptInvitation() {
  const { t } = useLang();
  const { token } = useParams();
  const nav = useNavigate();
  const { setSession } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/invitations/token/${token}`)
      .then((r) => setInvite(r.data))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error(t("password_mismatch")); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/invitations/token/${token}/accept`, { password: pw }, { withCredentials: true });
      setSession(data);
      toast.success(t("invite_accepted"));
      nav("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grain relative flex items-center justify-center px-4">
      <div className="w-full max-w-md relative z-[2]">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-16 h-16 rounded-2xl overflow-hidden flex items-center justify-center flex-shrink-0" style={{ background: "#0A3F4A" }}>
            <img src="/cen21-logo.webp" alt="Cen21" className="w-full h-full object-contain" style={{ transform: "scale(2.4)" }} />
          </div>
          <div className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>{t("app_name")}</div>
        </div>
        <div className="glass rounded-3xl p-8" data-testid="accept-invite-card">
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: "Outfit" }}>{t("invite_title")}</h1>
          {error && <p data-testid="invite-error" className="text-sm text-rose-300 mt-4">{error}</p>}
          {!error && !invite && <p className="text-sm text-slate-400 mt-4">…</p>}
          {invite && (
            <>
              <p className="text-sm text-slate-400 mb-5">{t("invite_hint")} <span className="text-slate-200">{invite.invited_by_name}</span></p>
              <div className="glass rounded-xl p-3 mb-5 text-sm flex items-center justify-between gap-3" data-testid="invite-summary">
                <div className="min-w-0">
                  <div className="font-medium truncate">{invite.name}</div>
                  <div className="text-xs text-slate-400 truncate">{invite.email}</div>
                </div>
                <Badge variant="outline" className="border-indigo-400/30 bg-indigo-400/10 text-indigo-200 whitespace-nowrap">{t(invite.role)}</Badge>
              </div>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="text-slate-300">{t("new_password")}</Label>
                  <Input data-testid="invite-password-input" type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1.5 bg-white/5 border-white/10" />
                </div>
                <div>
                  <Label className="text-slate-300">{t("confirm_password")}</Label>
                  <Input data-testid="invite-password-confirm-input" type="password" required minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-1.5 bg-white/5 border-white/10" />
                </div>
                <Button type="submit" data-testid="invite-submit-btn" disabled={loading} className="w-full btn-glow bg-indigo-500 hover:bg-indigo-600">{loading ? "…" : t("invite_activate")}</Button>
              </form>
            </>
          )}
          <div className="mt-6 text-sm text-center">
            <Link data-testid="invite-back-to-login" to="/login" className="text-indigo-400 hover:text-indigo-300">{t("login")}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
